/**
 * Shared helper for deferred Section Rendering API work.
 *
 * Provides three things the theme's ad-hoc hydration call sites lack:
 *   - scheduling: run deferred work after document ready, during idle time,
 *     so it stops competing with critical page-load resources
 *   - concurrency capping: queue section fetches so a page with many product
 *     cards cannot saturate the connection pool
 *   - a shared promise cache: identical section requests resolve once
 *
 * Callers that are user-initiated (quick view, predictive search) must pass
 * priority 'high' so their request bypasses the queue.
 *
 * Usage:
 *   FoxTheme.hydration.schedule(() => { ... })
 *   FoxTheme.hydration.whenVisible(el, () => { ... })
 *   FoxTheme.hydration.fetchSection(url, 'media-replacements').then((doc) => { ... })
 *   FoxTheme.hydration.fetchSection(url, 'quick-view', { priority: 'high' })
 */
window.FoxTheme = window.FoxTheme || {};

if (!window.FoxTheme.hydration) {
  window.FoxTheme.hydration = (function () {
    const MAX_CONCURRENT = 4;
    const IDLE_TIMEOUT = 2000;
    const VISIBLE_MARGIN = '200px 0px 200px 0px';

    const cache = new Map();
    const pending = [];
    let active = 0;

    const requestIdle =
      typeof window.requestIdleCallback === 'function'
        ? (fn) => window.requestIdleCallback(fn, { timeout: IDLE_TIMEOUT })
        : (fn) => setTimeout(fn, 1);

    function whenReady(fn) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', fn, { once: true });
      } else {
        fn();
      }
    }

    function schedule(fn) {
      whenReady(() => requestIdle(fn));
    }

    function whenVisible(element, fn, options) {
      if (!element) return function () {};

      if (typeof IntersectionObserver !== 'function') {
        schedule(fn);
        return function () {};
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            requestIdle(fn);
          });
        },
        { rootMargin: (options && options.rootMargin) || VISIBLE_MARGIN }
      );

      observer.observe(element);

      return function () {
        observer.disconnect();
      };
    }

    function drain() {
      while (active < MAX_CONCURRENT && pending.length) {
        const task = pending.shift();
        active++;
        task().finally(() => {
          active--;
          drain();
        });
      }
    }

    function enqueue(task) {
      return new Promise((resolve) => {
        pending.push(() => task().then(resolve));
        drain();
      });
    }

    function buildUrl(url, sectionId) {
      let resolved;
      try {
        resolved = new URL(url || window.location.pathname, window.location.origin);
      } catch (error) {
        return null;
      }
      if (resolved.origin !== window.location.origin) return null;
      resolved.searchParams.set('section_id', sectionId);
      return resolved.toString();
    }

    function request(href) {
      return fetch(href)
        .then((response) => (response.ok ? response.text() : null))
        .catch(() => null);
    }

    function fetchSectionHTML(url, sectionId, options) {
      const settings = options || {};
      const href = buildUrl(url, sectionId);

      if (!href) return Promise.resolve(null);
      if (settings.cache !== false && cache.has(href)) return cache.get(href);

      const promise = settings.priority === 'high' ? request(href) : enqueue(() => request(href));

      if (settings.cache !== false) cache.set(href, promise);

      return promise;
    }

    function fetchSection(url, sectionId, options) {
      return fetchSectionHTML(url, sectionId, options).then((html) => {
        if (!html) return null;
        return new DOMParser().parseFromString(html, 'text/html');
      });
    }

    return {
      schedule,
      whenVisible,
      fetchSection,
      fetchSectionHTML,
    };
  })();
}
